import { json, LoaderFunction, redirect, MetaFunction } from "@remix-run/node";
import {
  useActionData,
  useLoaderData,
  useNavigate,
  useNavigation,
} from "@remix-run/react";
import { useTranslation } from "react-i18next";
import { checkAuth, login } from "~/utils/auth.server";
import i18next from "~/i18next.server";
import { Button } from "~/components/general/Button";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useForm, validationError } from "@rvf/remix";
import { withZod } from "@rvf/zod";
import { FormInput } from "~/components/general/FormInput";
import logo from "~/assets/logo.svg";
import { toast } from "sonner";
import { AlertDescription } from "~/components/ui/alert";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Dialog } from "~/components/ui/dialog";
import { Alert } from "~/components/ui/alert";
import { AlertCircle, X } from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "Login Partner" },
    {
      name: "description",
      content: "Login Partner",
    },
  ];
};

export const loginSchema = withZod(
  z.object({
    number: z.string().min(1, "error.login.number"),
    password: z.string().min(1, "error.login.password"),
  })
);

type ActionData = {
  errors?: {
    number?: string[] | undefined;
    password?: string[] | undefined;
  };
  success?: boolean;
  error?: string;
};

export const loader: LoaderFunction = async ({ request }) => {
  try {
    const userToken = await checkAuth(request);
    if (userToken) {
      return redirect("/contract");
    }
    return { userToken };
  } catch (error) {
    console.error(error);
    return { userToken: null };
  }
};

export const action = async ({ request }: { request: Request }) => {
  const t = await i18next.getFixedT(request);
  const formData = await request.formData();
  const number = (formData.get("number") as string) ?? "";
  const password = (formData.get("password") as string) ?? "";
  const result = await loginSchema.validate({
    number,
    password,
  });

  if (result.error) {
    return validationError(result.error, result.submittedData);
  }
  try {
    const cookieHeader = await login(number, password);
    return json(
      { success: true },
      {
        headers: {
          "Set-Cookie": cookieHeader,
        },
      }
    );
  } catch (error) {
    console.error(error);
    return json({ error: t("error.login.invalid") }, { status: 401 });
  }
};

const NotificationPopup = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const deploymentLevel = import.meta.env.VITE_DEPLOYMENT_LEVEL;

  if (deploymentLevel !== 'DEV') {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {t("label.login.notification.title")}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-base">
          {t("label.login.notification.description")}
        </DialogDescription>
        <DialogFooter>
          <Button
            onClick={() => window.open('https://portal.sgb-energiehandel.com/', '_blank')}
          >
            {t("label.login.notification.visitPortal")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Login() {
  useLoaderData<typeof loader>();
  const actionData = useActionData<ActionData>();
  const form = useForm({ validator: loginSchema, method: "post" });
  const { t } = useTranslation();
  const navigation = useNavigation();
  const navigate = useNavigate();
  useEffect(() => {
    if (actionData?.success) {
      toast.success(t("toast.login.success"));
      navigate("/");
    }
    if (actionData?.error) {
      toast.error(t(actionData.error));
    }
  }, [actionData, t]);
  return (
    <>
      <NotificationPopup />
      <form {...form.getFormProps()}>
        <div
          className={
            "flex h-screen flex-1 items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100"
          }
        >
          <div
            className={
              "flex w-11/12 md:w-8/12 lg:w-5/12 min-w-96 flex-col bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200"
            }
          >
            <div className="bg-gray-50 py-6 border-b border-gray-200">
              <img src={logo} alt="Logo" className="mx-auto mb-4 h-12 w-auto" />
              <h1
                className={"text-center text-gray-700 text-2xl font-semibold"}
              >
                {t("label.login.title")}
              </h1>
            </div>
            <div className={"p-8 md:p-10 flex flex-1 flex-col gap-5"}>
              <FormInput
                scope={form.scope("number")}
                placeholder={t("placeholders.login.username")}
                label={t("label.login.username")}
                type="text"
                name="number"
                className="transition-all duration-200 hover:border-gray-400"
              />
              <FormInput
                scope={form.scope("password")}
                placeholder={t("placeholders.login.password")}
                label={t("label.login.password")}
                type="password"
                name="password"
                id="password"
                className="transition-all duration-200 hover:border-gray-400"
              />
              <Button
                isLoading={navigation.state !== "idle"}
                className={
                  "flex w-full md:w-4/12 self-end transition-all duration-200 py-2.5 rounded-md"
                }
                type="submit"
              >
                {t("label.login.login")}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
